/**
 * Cart State Manager — auto-removes OOS items when user changes state.
 */
import { fetchStockByState, isAvailableForState } from '../../utils/stock-availability';
import { STATE_EVENTS } from '../../utils/state-events';

// Wait for rivets / Vue to re-render the cart drawer after the AJAX update
// before we inject the removal banner. 300ms is enough on staging; if the
// drawer skin in production renders faster or slower this can be tuned.
const REMOVAL_NOTICE_INSERT_DELAY_MS = 300;
// Cart drawer auto-dismisses the banner after this many ms.
const REMOVAL_NOTICE_LIFETIME_MS = 15000;
// Debounce window between rapid state changes so we only run the removal
// flow once per "settled" selection.
const STATE_CHANGE_DEBOUNCE_MS = 100;

async function removeOosItemsForState(state) {
  if (!state || !window.CartJS) return;

  try {
    const cart = await (await fetch('/cart.js')).json();
    if (!cart || !cart.items || cart.items.length === 0) return;

    // Get unique product handles (skip samples)
    const handleSet = new Set();
    cart.items.forEach((item) => {
      if (item.properties && item.properties._sample === 'true') return;
      handleSet.add(item.handle);
    });

    // Fetch stock data in parallel
    const stockMap = {};
    await Promise.all(
      Array.from(handleSet).map(async (handle) => {
        const data = await fetchStockByState(handle);
        if (data) stockMap[handle] = data;
      })
    );

    // Find OOS items
    const itemsToRemove = [];
    cart.items.forEach((item, index) => {
      if (item.properties && item.properties._sample === 'true') return;
      const stockData = stockMap[item.handle];
      if (!stockData) return;
      if (isAvailableForState(stockData, item.variant_id, state) === false) {
        itemsToRemove.push({
          index: index + 1,
          title: item.title,
          image: item.image || '',
          variantId: item.variant_id,
        });
      }
    });

    if (itemsToRemove.length === 0) return;

    // Batch remove via Shopify AJAX (not CartJS — it triggers page reload)
    const updates = {};
    itemsToRemove.forEach((item) => { updates[item.variantId] = 0; });
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });

    // Sync CartJS + trigger rivets re-render
    const freshCart = await (await fetch('/cart.js')).json();
    Object.assign(CartJS.cart, freshCart);
    $(document).trigger('cart.requestComplete', [freshCart]);

    // Open cart drawer, wait for rivets re-render, then insert notice
    const cartBtn = document.querySelector('.new-layout-header__actions-cart');
    if (cartBtn) cartBtn.click();
    setTimeout(() => showRemovalNotice(itemsToRemove), REMOVAL_NOTICE_INSERT_DELAY_MS);
  } catch (err) {
    // Swallow: removal failure should not break the storefront. The visible
    // outcome (cart still has the OOS item) is still safe — Shopify shipping
    // validation blocks the order at checkout.
  }
}

// Build the removal notice safely. Avoid innerHTML — variant titles come from
// /cart.js (admin-controlled today) but we still defend against future changes
// or 3rd-party metafield titles by escaping via textContent and DOM creation.
function showRemovalNotice(items) {
  document.querySelectorAll('.cart-state-removal-notice').forEach((el) => el.remove());

  const container = document.createElement('div');
  container.className = 'cart-state-removal-notice';

  items.forEach((item) => {
    const inner = document.createElement('div');
    inner.className = 'cart-state-removal-notice__inner';

    if (item.image) {
      const img = document.createElement('img');
      img.className = 'cart-state-removal-notice__image';
      img.src = item.image;
      img.alt = item.title;
      inner.appendChild(img);
    }

    const p = document.createElement('p');
    p.className = 'cart-state-removal-notice__text';
    p.textContent = `We are unable to ship '${item.title}' as it is not available in your state. This has been removed from your bag.`;
    inner.appendChild(p);

    container.appendChild(inner);
  });

  // Insert at top of cart content (after notice banner area)
  const noticeBanner = document.querySelector('.cart-drawer__header-notice-banner');
  const contentForm = document.querySelector('.cart-drawer__content__form-inner');
  if (noticeBanner) {
    noticeBanner.parentNode.insertBefore(container, noticeBanner.nextSibling);
  } else if (contentForm) {
    contentForm.insertBefore(container, contentForm.firstChild);
  }

  setTimeout(() => container.remove(), REMOVAL_NOTICE_LIFETIME_MS);
}

export default function CartStateManager() {
  let debounceTimer;
  window.addEventListener(STATE_EVENTS.SELECTED, (e) => {
    clearTimeout(debounceTimer);
    const state = e.detail && e.detail.state;
    if (!state) return;
    debounceTimer = setTimeout(() => removeOosItemsForState(state), STATE_CHANGE_DEBOUNCE_MS);
  });
}
